// src/modules/app-views/_apps/appmulti/quotations/Form/Steps/Modals/PriceAdjustmentModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Descriptions, Modal, Row } from 'antd';
import { strToNumber } from 'utils/helpers';
import translateX from 'utils/translateX';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import financialService from 'services/financialService';
import useZustand from 'hooks/useZustand';
import { calculatePlanValues } from 'modules/app-views/_apps/appmulti/services/pricingService';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const hiddenButtonStyle = { display: 'none' };
const priceSuffix = [{ value: 'price', label: 'R$' }];

// *******************************************************************************
// COMPONENT — inner modal (inside SimpleFormProvider)
// *******************************************************************************
const ModalComponent = forwardRef((props, ref) => {
  // HOOKS
  const { form, goFocus } = useSimpleFormContext();

  const {
    plan,
    showModal,
    setShowModal,
    isDisabled,
    total,
    documentContext,
    travellers,
    setPlan,
    paymentSelected,
    creditCardInstallment,
  } = props;
  const { filter } = documentContext || {};

  // STATE
  const initialState = {
    fullPrice: 0,
    standardDiscount: 0,
    standardDiscountPercentage: 0,
    promotionalPrice: 0,
    finalPrice: 0,
    adjustment: 0,
    adjustedPricePercentage: 0,
    adjustedPrice: 0,
    unit: 'price',
  };
  const [state, setState] = useState(initialState);

  // EFFECTS
  useEffect(() => {
    if (!plan || !showModal) return;
    calculateAdjustedPrice();
    goFocus('price', true);
  }, [plan, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({ state }));

  // HANDLERS
  const onClose = () => setShowModal(false);
  const onChangeValues = () => calculateAdjustedPrice();
  const onChangeUnit = unit => calculateAdjustedPrice(unit);

  const calculateAdjustedPrice = (unit = state.unit || 'price') => {
    const fullPrice = total?.resellerGrossPrice || 0;
    const promotionalPrice = total?.resellerNetPrice || 0;
    const totalToPay = total?.totalToPay || 0;

    const standardDiscount = fullPrice - promotionalPrice;
    const standardDiscountPercentage = financialService.diffMarkdown(
      fullPrice,
      promotionalPrice,
    );

    const discountCoupon = total?.couponDiscount?.discountValue || 0;
    const finalPrice = promotionalPrice - discountCoupon;

    let adjustedPrice = !isNaN(form?.getFieldValue('price'))
      ? Number(form?.getFieldValue('price'))
      : totalToPay || 0;
    if (unit === 'price' && adjustedPrice < 0) adjustedPrice = 0;

    const adjustedPricePercentage = financialService.diffMarkdown(
      finalPrice,
      adjustedPrice,
      3,
    );
    const adjustment = adjustedPrice - finalPrice;

    setState({
      fullPrice: financialService.formatMoney(fullPrice),
      standardDiscount: standardDiscount
        ? financialService.formatMoney(standardDiscount)
        : '-',
      standardDiscountPercentage: standardDiscount
        ? financialService.formatNumber(standardDiscountPercentage, {
            decimals: 3,
          })
        : '-',
      promotionalPrice: financialService.formatMoney(promotionalPrice),
      finalPrice: financialService.formatMoney(finalPrice),
      adjustment: adjustment
        ? financialService.formatMoney(adjustment, 2)
        : '-',
      adjustedPricePercentage: adjustment
        ? financialService.formatNumber(adjustedPricePercentage, {
            decimals: 3,
          })
        : '-',
      adjustedPrice: adjustment
        ? financialService.formatMoney(adjustedPrice)
        : financialService.formatMoney(finalPrice),
      unit: 'price',
    });
  };

  // RENDER
  const descriptionItems = [
    {
      key: 'fullPrice',
      label: translateX('full_price'),
      children: state.fullPrice,
      span: 2,
    },
    {
      key: 'standardDiscount',
      label: translateX('standard_discount'),
      children: state.standardDiscount,
    },
    {
      key: 'standardDiscountPercentage',
      label: '%',
      children: state.standardDiscountPercentage,
    },
    {
      key: 'promotionalPrice',
      label: translateX('promotional_price'),
      children: state.promotionalPrice,
      span: 2,
    },
    {
      key: 'finalPrice',
      label: translateX('final_price'),
      children: state.finalPrice,
      span: 2,
    },
    {
      key: 'adjustment',
      label: translateX('adjust'),
      children: state.adjustment,
    },
    {
      key: 'adjustedPricePercentage',
      label: '%',
      children: state.adjustedPricePercentage,
    },
    {
      key: 'adjustedPrice',
      label: translateX('adjusted_price'),
      children: state.adjustedPrice,
      span: 2,
    },
  ];

  return (
    <Modal
      cancelText={translateX(isDisabled ? 'close' : 'cancel')}
      title={
        <div className="text-muted text-uppercase mb-4">
          {translateX('price_adjustment')}
        </div>
      }
      open={showModal}
      okText={translateX(!isDisabled ? 'confirm' : 'close')}
      onCancel={onClose}
      cancelButtonProps={isDisabled ? { style: hiddenButtonStyle } : undefined}
      onOk={() => (isDisabled ? onClose() : form.submit())}
      getContainer={false}
      okType={isDisabled ? 'default' : 'primary'}
      afterClose={() => {
        const summary = calculatePlanValues({
          plan,
          days: filter.days,
          passengersArr: travellers,
          paymentMethod: paymentSelected,
          creditCardInstallment,
        });
        setPlan && setPlan(prev => ({ ...prev, summary: { ...summary } }));
      }}
    >
      <Descriptions
        bordered
        title={translateX('summary')}
        size="small"
        items={descriptionItems}
        column={2}
      />

      {!isDisabled ? (
        <Row justify="center" align="middle" className="pt-5 pr-5 pb-2 pl-5">
          <SimpleNumber
            // eslint-disable-next-line jsx-a11y/no-autofocus -- legacy parity: price auto-focus on open
            autoFocus
            disabled={isDisabled}
            name="price"
            label="adjusted_price"
            suffix={priceSuffix}
            suffixDefaultValue={state.unit}
            onChange={onChangeValues}
            onSuffixChange={onChangeUnit}
          />
        </Row>
      ) : null}
    </Modal>
  );
});

ModalComponent.displayName = 'PriceAdjustmentModalComponent';

// *******************************************************************************
// COMPONENT — main (provider + imperative open)
// *******************************************************************************
export const MultiQuotationPriceAdjustmentModal = forwardRef((props, ref) => {
  // HOOKS
  const {
    setPlan,
    setAdminAdjustment,
    setAdminDiscount,
    total = props?.plan?.summary?.total,
  } = props;

  const price = financialService.formatNumber(total?.totalToPay);
  const modalRef = useRef(null);
  const user = useZustand('auth', 'user');

  // STATE
  const [showModal, setShowModal] = useState(false);

  useImperativeHandle(ref, () => ({ showModal, setShowModal }));

  // HANDLERS
  const onConfirm = async () => {
    const { state } = modalRef.current || {};
    const adjustedPrice = strToNumber(state?.adjustedPrice);
    const adjustment = strToNumber(state?.adjustment);
    const adjustedPricePercentage = strToNumber(state?.adjustedPricePercentage);

    if (adjustedPricePercentage > 0) {
      const adminDiscount = {
        type: 'value',
        accountId: null,
        value: 0,
        percentage: 0,
      };
      const adminAdjustment = {
        type: 'value',
        accountId: user?._id,
        value: adjustment,
        percentage: adjustedPricePercentage,
      };
      setAdminAdjustment(adminAdjustment);
      setAdminDiscount(adminDiscount);
      setPlan &&
        setPlan(prev => ({
          ...prev,
          summary: {
            ...prev.summary,
            total: {
              ...prev.summary.total,
              adminDiscount,
              adminAdjustment,
              adjustedPrice,
            },
          },
        }));
    } else if (adjustedPricePercentage < 0) {
      const adminDiscount = {
        type: 'value',
        accountId: user?._id,
        value: -adjustment,
        percentage: -adjustedPricePercentage,
      };
      const adminAdjustment = {
        type: 'value',
        accountId: null,
        value: 0,
        percentage: 0,
      };
      setAdminAdjustment(adminAdjustment);
      setAdminDiscount(adminDiscount);
      setPlan &&
        setPlan(prev => ({
          ...prev,
          summary: {
            ...prev.summary,
            total: {
              ...prev.summary.total,
              adminDiscount,
              adminAdjustment,
              adjustedPrice,
            },
          },
        }));
    }

    setShowModal(false);
    return false; // abort provider auto-save
  };

  // RENDER
  return (
    <SimpleFormProvider
      name={`form-price-${+new Date()}`}
      showSubmitButton={false}
      requiredFields={[]}
      initialValues={{ price }}
      onFinish={onConfirm}
    >
      <ModalComponent
        {...props}
        showModal={showModal}
        setShowModal={setShowModal}
        ref={modalRef}
      />
    </SimpleFormProvider>
  );
});

MultiQuotationPriceAdjustmentModal.displayName =
  'MultiQuotationPriceAdjustmentModal';

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationPriceAdjustmentModal;
